import type { ErrorNote, Job, JobStatusCondition, JobStatusState, JobStatusTrack } from "../types/Job";

/**
 * Shapes the new (central-nervous-system) job status for display.
 *
 * The three chains — data, logistics and billing — advance independently, each through a fixed
 * order. What the calculator records per chain is the status it reached, the one after it in that
 * order, and the conditions weighed by the deepest check that ran. Because the checks nest (a check
 * runs only once the one below it passed) and each clears its predecessor's conditions, the
 * surviving set is exactly what gates `next` — so "why hasn't it moved on" needs no derivation
 * here, only presentation.
 */

/** The chains in the order they're shown, widest-reaching first. */
export const JOB_STATUS_TRACKS = ["data", "logistics", "billing"] as const;

export type JobStatusTrackKey = (typeof JOB_STATUS_TRACKS)[number];

export const JOB_STATUS_TRACK_LABELS: Record<JobStatusTrackKey, string> = {
	data: "Data",
	logistics: "Logistics",
	billing: "Billing",
};

export interface JobStatusTrackSummary {
	key: JobStatusTrackKey;
	label: string;
	/** The status this chain has reached. Null only on a chain the calculator never recorded. */
	current: string | null;
	/** When `current` was set, if the calculator recorded one. */
	currentDate: Date | null;
	/** The status after `current`. Null at the end of the chain — nothing left to advance to. */
	next: string | null;
	/** Every condition weighed by the check gating `next`, unmet ones first so the blockers lead. */
	conditions: JobStatusConditionSummary[];
	/**
	 * The furthest status this chain reached, when it has since fallen back to an earlier one —
	 * null on the normal case of a chain sitting at its own high-water mark. Worth separating
	 * from an ordinary hold-up: a chain that never advanced is waiting on something, while one
	 * that regressed had finished work undone, usually by an edit made long afterwards.
	 */
	regressedFrom: string | null;
	/** When the chain was last at `regressedFrom`. */
	regressedFromDate: Date | null;
	/**
	 * Why this chain is not advancing, as a state the view can switch on:
	 *  - `complete` — no `next`; the chain has run its course.
	 *  - `blocked` — at least one condition is unmet.
	 *  - `ready` — every condition is met, so the next run should advance it. Seen between a
	 *    condition being satisfied and the calculator next running.
	 *  - `unknown` — no conditions recorded, so there is nothing to say about the hold-up.
	 */
	state: "complete" | "blocked" | "ready" | "unknown";
}

/** A condition, plus whether being unmet means it *failed* rather than being outstanding. */
export interface JobStatusConditionSummary extends JobStatusCondition {
	failed: boolean;
	/**
	 * True when `jobStatusConditionLabel` already reads as the failure, so anything restating it —
	 * the screen-reader suffix — would say the same thing twice. Carried rather than left to the
	 * view to infer from the label text, which would silently follow any rewording of the copy.
	 */
	labelStatesFailure: boolean;
}

export interface JobStatusSummary {
	/** The calculated roll-up of the three chains. */
	internal: string | null;
	/** The roll-up's customer-facing simplification. Carried because it is part of the status the
	 *  calculator writes, though the section shows only `internal` — the two agree on all but the
	 *  statuses that have no customer equivalent (Error keeps the previous customer value). */
	customer: string | null;
	tracks: JobStatusTrackSummary[];
	/**
	 * What the status calculator itself said went wrong, if anything.
	 *
	 * These are already rendered with every other source's errors in the panel's Errors section,
	 * and are repeated here deliberately: they are the only thing that explains a contradiction
	 * *between* two chains (a chain being behind where another one requires it to be), which the
	 * per-chain cards cannot show on their own because each knows only about itself. The two
	 * sections are also gated separately, so neither can be relied on to carry the other.
	 */
	calculatorNotes: string[];
}

/**
 * The condition whose being unmet means something went wrong rather than something not having
 * happened yet: the calculator records an error on the chain and holds it there.
 */
const ERROR_CONDITION = "No Errors";

/** The source the status calculator signs its errors with, in the job's software errors. */
export const STATUS_CALCULATOR_SOURCE = "Status Calculator";

/**
 * How the job's `Data Sent` marks a send that was attempted and failed, as opposed to one that
 * simply has not run. The rest of the value is the condition name verbatim, which is what lets
 * the two be matched up at all.
 */
const FAILED_SEND_PREFIX = "FAILED - ";

/** True for a condition that, unmet, reports a failure rather than outstanding work. */
function isErrorCondition(condition: string): boolean {
	return condition === ERROR_CONDITION;
}

/**
 * Which of a job's conditions are known to have been *tried and failed*.
 *
 * A data-send condition reads the same whether the send has not happened yet or happened and
 * errored — `met: false` either way — and the difference is recorded somewhere else entirely:
 * `Data Sent` carries the failed attempt as "FAILED - " + the condition name. Both come from the
 * calculator's own vocabulary, so the names line up exactly; this is the join.
 *
 * That makes it a convention rather than a contract, and one this repo does not own. If the
 * calculator ever changes the prefix this quietly goes back to showing failures as pending work,
 * which is why the prefix is pinned by a test.
 */
function failedSends(dataSent: readonly string[] | undefined): Set<string> {
	const failed = new Set<string>();
	for (const entry of dataSent ?? []) {
		if (entry.startsWith(FAILED_SEND_PREFIX)) failed.add(entry.slice(FAILED_SEND_PREFIX.length));
	}
	return failed;
}

/**
 * How a condition reads on screen.
 *
 * The calculator names its conditions for the state that satisfies them, which reads back-to-front
 * on the error condition: an unmet "No Errors" is precisely a job that HAS an error, and the double
 * negative is what makes the row hard to read. Failed, it says "Error" instead; met, "No Errors" is
 * already the plain truth. Every other condition already names itself well enough — a failed send
 * is told apart by how the row is toned, not by rewording it.
 */
export function jobStatusConditionLabel(condition: JobStatusCondition): string {
	return !condition.met && isErrorCondition(condition.condition) ? "Error" : condition.condition;
}

/** Unmet first — the blockers are the reason to read the list at all — each group keeping its recorded order. */
function blockersFirst(conditions: JobStatusConditionSummary[]): JobStatusConditionSummary[] {
	return [...conditions.filter((c) => !c.met), ...conditions.filter((c) => c.met)];
}

/** An ISO timestamp as a Date, or null if it is absent or unparseable. */
function parseDate(value: string | undefined): Date | null {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

function summarizeTrack(key: JobStatusTrackKey, track: JobStatusTrack, failed: Set<string>): JobStatusTrackSummary {
	const conditions = blockersFirst(
		(track.conditions ?? []).map((c) => ({
			...c,
			failed: !c.met && (isErrorCondition(c.condition) || failed.has(c.condition)),
			// The one case `jobStatusConditionLabel` rewrites — kept in step with it by a test.
			labelStatesFailure: !c.met && isErrorCondition(c.condition),
		})),
	);
	const anyUnmet = conditions.some((c) => !c.met);
	const next = track.next ?? null;
	return {
		key,
		label: JOB_STATUS_TRACK_LABELS[key],
		current: track.current ?? null,
		currentDate: parseDate(track.currentDate),
		next,
		conditions,
		regressedFrom: track.regressedFrom ?? null,
		regressedFromDate: parseDate(track.regressedFromDate),
		state: !next ? "complete" : anyUnmet ? "blocked" : conditions.length > 0 ? "ready" : "unknown",
	};
}

/** The notes the status calculator itself wrote, out of every source's errors on the job. */
function calculatorNotes(errors: readonly ErrorNote[] | undefined): string[] {
	return (errors ?? []).filter((e) => e.source?.trim() === STATUS_CALCULATOR_SOURCE).map((e) => e.note);
}

/**
 * The job's new status, ready to render — or null when there is nothing to show.
 *
 * Null covers two different situations the panel has no way to tell apart, and shouldn't try to:
 * the job has never been through the calculator (the common one — it only backfills jobs created
 * from 2026-08-01), or the job reached the panel from a lite list payload, which leaves the status
 * field out. Either way the honest answer is that this job has no status data here.
 */
export function summarizeJobStatus(
	job: Pick<Job, "statusInternal" | "statusCustomer" | "statusState" | "dataSent" | "softwareErrors">,
): JobStatusSummary | null {
	const state: JobStatusState | undefined = job.statusState;
	if (!state) return null;
	const failed = failedSends(job.dataSent);
	return {
		internal: job.statusInternal ?? null,
		customer: job.statusCustomer ?? null,
		tracks: JOB_STATUS_TRACKS.map((key) => summarizeTrack(key, state[key], failed)),
		calculatorNotes: calculatorNotes(job.softwareErrors),
	};
}
